import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, mergeProps as _mergeProps, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "contentHeader flex" }
const _hoisted_2 = { class: "contentBox" }
const _hoisted_3 = { class: "block w-full" }
const _hoisted_4 = { class: "flex mb-4" }
const _hoisted_5 = ["title"]
const _hoisted_6 = ["disabled", "title"]
const _hoisted_7 = { class: "ml-auto" }
const _hoisted_8 = { class: "text-center" }

import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { BryntumGrid } from "@bryntum/grid-vue-3";
import {Menu, Store, StringHelper} from "@bryntum/grid";
import GridInputFilter from "@/components/GridInputFilter";
import { useGlobalStore } from "@/stores/GlobalStore";
import { ErrorLocationType, PopupType, TableType } from '@/utilities/enums/Enums';
import { $t } from "@/utilities/i18n/config";
import {PermissionService} from "@/utilities/services/PermissionService";
import dialog from "@/dialog";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import { GeneralResponse } from "@/models/api/Responses";
import Error from "@/components/Error";
import { ControlCenterInfo } from "@/models/controlCenter/ControlCenterInfo";
import {useI18n} from "vue-i18n";
import {checkboxColumnConfig, defaultSpaceAroundTableOnPage} from "@/AppConfig";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {ControlCenterService} from "@/utilities/services/entities/ControlCenterService";



export default /*@__PURE__*/_defineComponent({
  __name: 'ControlCenters',
  setup(__props) {

const { t } = useI18n();
const gridStore = new Store();
const globalStore = useGlobalStore();

let selectedControlCenters = ref([]);
const controlCenterService = new ControlCenterService();
const permissionService = new PermissionService();

const deleteSelection = async() => {
  //drop call without selection
  if(!selectedControlCenters.value?.some(()=>true)) return;

  const answer = await dialog
      .okText(t("global.delete"))
      .cancelText(t("global.cancel"))
      .confirm(t("controlCenters.confirmDeletionMulti", { count:selectedControlCenters.value.length}));

  if (answer) {
    let someEntriesFailed = false;
    for (const cc of selectedControlCenters.value){
      const resp = await controlCenterService.deleteControlCenter(cc.id);

      if(resp === null || resp === undefined || resp?.statusCode !== 0){
        ErrorHandler.handleError(resp, ErrorLocationType.table);
        someEntriesFailed = true;
        continue;
      }

      gridStore.applyChangeset({removed:[{id:cc.id}]});
    }

    if (someEntriesFailed) return;

    globalStore.showSuccess(t("global.deleted"));
  }
};

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage}px`;
};

const grid = ref(null);
const useGridConfig = {
  headerMenuFeature: false,
  enableTextSelection: true,
  selectionMode : {
    row	  : true,
    checkbox: checkboxColumnConfig,
    showCheckAll : true,
    deselectFilteredOutRecords: true
  },
  columns: {
    data: [
      { id:'id', field: 'id', text: t('controlCenters.grid.id'), width: 36, },
      {
        id:'syncId',
        field: 'syncId',
        text: t('controlCenters.grid.syncId'),
        flex: 1,
        type: "template",
        template: ({ record }) => {
					return StringHelper.xss`${record.data.syncId ?? ""}`;
        },
      },
      { id:'name', field: 'name', text: t('controlCenters.grid.name'), flex: 1, },
      { id:'phoneNumber', field: 'phoneNumber', text: t('controlCenters.grid.phoneNumber'), flex: 1, },
      {
        id:'locations',
        field: 'locations',
				sortable: false,
        text: '',
        width   : 36,
        type: "template",
        template: ({ record }) => {
					if(record.data.locations.length > 0)
						return `<div class='gridRowButton'><span class='icon-pin_outline'></span></div>`;
					return
				},
      },
      {
        id:'menu',
        text	: '',
        width   : 36,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-kontext_horizontal',
          onAction : ({ source: btn }) => {
            const menu = menuFactory(btn.cellInfo.record.data);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      },
    ],
    listeners:{
      async add(event:any){
        if(event.records.length === 1 && event.isMove[event.records[0].id]){
          await globalStore.saveUserColumnOrder(TableType[TableType.controlCenter], event.records[0].id, event.index.toString());
        }
      },
    }
  },
  store: gridStore,
  listeners: {
    selectionChange(event) {
      if(event.selection.length > 0 && event.selection[0]?.data === undefined) return;
      selectedControlCenters.value = selectedControlCenters.value.concat(event.selected.map(cr => cr.data))
      selectedControlCenters.value = selectedControlCenters.value.filter(c => !event.deselected.map(cr => cr.data).some(cr => c?.id === cr?.id));
    },
    datachange(){
      globalStore.currentItems = gridStore.count;
      globalStore.allItems = gridStore.originalCount;
    },
    cellClick({column, record}) {
      if (column.data.field === 'locations' && record.data.locations.length > 0) {
        globalStore.openPopup({
          data: record.data,
          action: globalStore.closeModal,
          title: "Standorte",
          type: PopupType.group2Locations
        });
      }
    },
  },
  height: currentWindowHeight,
}
const gridConfig = reactive(useGridConfig);

const menuFactory = (record:object) => {
  const menu = new Menu({
    anchor   : true,
    autoShow : true,
    items	: [
      {
        text: t('global.edit'),
        cls: 'color',
        icon: 'icon-editieren',
        hidden: !permissionService.userHasPermission("ControlCenter.Edit"),
        weight: 100,
        onItem : ({ menu }) => {
          globalStore.openPopup({
            data:menu.record,
            action:addOrUpdateControlCenter,
            resetButton:false,
            title:$t("popups.controlCenterEditor.title.edit"),
            type:PopupType.controlCenterEditor
          });
        }
      },
      {
        text: t('global.delete'),
        hidden: !permissionService.userHasPermission("ControlCenter.Delete"),
        cls	: 'color',
        icon: 'icon-loeschen',
        weight : 140,
        onItem: async ({ menu }) => {
          const answer = await dialog
              .okText(t("global.delete"))
              .cancelText(t("global.cancel"))
              .confirm(t("controlCenters.confirmDeletionSingle", {name:menu.record.name}));

          if (answer) {
            const resp = await controlCenterService.deleteControlCenter(menu.record.id);

            if(resp === null || resp === undefined || resp?.statusCode !== 0){
              ErrorHandler.handleError(resp, ErrorLocationType.table);
              return;
            }

            gridStore.applyChangeset({removed:[{id:menu.record.id}]});

            globalStore.showSuccess(t('global.deleted'));
          }
        }
      }
    ],
  });

  menu.setRecord(record);

  return menu;
};

const newControlCenter = () => {
  const controlCenter = new ControlCenterInfo();

  globalStore.openPopup({
    data:controlCenter,
    action:addOrUpdateControlCenter,
    resetButton:false,
    title:$t("popups.controlCenterEditor.title.new"),
    type:PopupType.controlCenterEditor
  });
}

const addOrUpdateControlCenter = async () => {
  const controlCenter = globalStore.modal.contentObject as ControlCenterInfo;

  const ccResp = await controlCenterService.addOrUpdateControlCenter(controlCenter);
  if (ccResp === null || ccResp === undefined || ccResp.statusCode !== 0){
    ErrorHandler.handleError(ccResp as GeneralResponse, ErrorLocationType.popup);
    return;
  }

  if(controlCenter.id < 0) controlCenter.id = ccResp.id;
  // fix selection when it was an update -> replace old value in selected-array
  else if(selectedControlCenters.value.some(el => el.id === controlCenter.id)) {
    selectedControlCenters.value = selectedControlCenters.value.filter(el => el.id !== controlCenter.id);
    selectedControlCenters.value.push(controlCenter);
  }

  // in case of an update bryntum automatically replaces the entry with the same ID
  gridStore.add(controlCenter);
  // reapply sorting
  gridStore.sort(gridStore.sorters);

  globalStore.closeModal();
  globalStore.showSuccess(t("global.saved"));
}

const setGridData = async ()=>{
  // start spinner
  globalStore.gridDataIsLoading = true;

  const controlCentersResp = await controlCenterService.getControlCenters("display");

  if (controlCentersResp === null || controlCentersResp === undefined || controlCentersResp.statusCode !== 0) {
    ErrorHandler.handleError(controlCentersResp, ErrorLocationType.table);
    return;
  }

  gridStore.setStoreData(controlCentersResp.controlCenterInfo);

  globalStore.reorderColumns(TableType[TableType.controlCenter]);

  globalStore.closeModal();
  // stop spinner
  globalStore.gridDataIsLoading = false;
};

onMounted(async () => {
  // calculate grid height and sub to window resize event
  recalcGridHeight();
  window.addEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(ref(gridStore));
  globalStore.setGridColumnStore(ref(grid.value?.instance.value?.columns));

  await setGridData();
})

onBeforeUnmount(()=>{
  window.removeEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(null);
  globalStore.setGridColumnStore(null);

  globalStore.currentItems = 0;
  globalStore.allItems = 0;
});

return (_ctx: any,_cache: any) => {
  const _directive_permission = _resolveDirective("permission")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h4", null, _toDisplayString(_unref($t)('controlCenters.title')), 1)
    ]),
    _createElementVNode("div", _hoisted_2, [
      _withDirectives(_createElementVNode("div", null, [
        _createVNode(_unref(Error), {
          errorLoc: _unref(ErrorLocationType).table
        }, null, 8, ["errorLoc"]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              onClick: newControlCenter,
              title: _unref(t)('controlcenter.newcontrolCenter')
            }, _cache[0] || (_cache[0] = [
              _createElementVNode("span", { class: "icon-neu" }, null, -1)
            ]), 8, _hoisted_5)), [
              [_directive_permission, 'ControlCenter.Create']
            ]),
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "primary mr-2",
              disabled: !_unref(selectedControlCenters).some(()=>true),
              onClick: deleteSelection,
              title: _unref(t)('global.delete')
            }, _cache[1] || (_cache[1] = [
              _createElementVNode("span", { class: "icon-loeschen" }, null, -1)
            ]), 8, _hoisted_6)), [
              [_directive_permission, 'ControlCenter.Delete']
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_unref(GridInputFilter), {
                gridStore: _unref(gridStore),
                popupType: _unref(PopupType).controlCenterFilter,
                hideFilterButton: true
              }, null, 8, ["gridStore", "popupType"])
            ])
          ]),
          _createVNode(_unref(BryntumGrid), _mergeProps({
            ref_key: "grid",
            ref: grid
          }, gridConfig, { readOnly: true }), null, 16)
        ])
      ], 512), [
        [_vShow, !_unref(globalStore).gridDataIsLoading]
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_8, [
        _createVNode(LoadingSpinner)
      ], 512), [
        [_vShow, _unref(globalStore).gridDataIsLoading]
      ])
    ])
  ]))
}
}

})